import React, { useCallback, useEffect, useState } from 'react'
import { contactLeadAdminService } from '../services/contactLeadAdminService'
import { PageHeader } from '../components/ui/PageHeader'
import { SearchField } from '../components/ui/SearchField'
import { DataTable } from '../components/ui/DataTable'
import { Pagination } from '../components/ui/Pagination'

function formatDateTime(isoOrDate) {
  if (!isoOrDate) return '—'
  const d = new Date(isoOrDate)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function cellText(value, maxLen = 48) {
  if (value === null || value === undefined || value === '') return '—'
  const s = String(value)
  if (s.length <= maxLen) return s
  return `${s.slice(0, maxLen)}…`
}

const PAGE_SIZE = 25

const defaultPagination = {
  currentPage: 1,
  itemsPerPage: PAGE_SIZE,
  totalItems: 0,
  totalPages: 1,
  hasNextPage: false,
  hasPrevPage: false,
}

const ContactLeads = () => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchDraft, setSearchDraft] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState(defaultPagination)

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const res = await contactLeadAdminService.listContactLeads({
        page,
        limit: PAGE_SIZE,
        search: appliedSearch,
      })
      const payload = res?.data
      const list = Array.isArray(payload?.contact_leads) ? payload.contact_leads : []
      const pg = payload?.pagination
      setRows(list)
      if (pg && typeof pg === 'object') {
        setPagination({
          currentPage: pg.currentPage ?? page,
          itemsPerPage: pg.itemsPerPage ?? PAGE_SIZE,
          totalItems: pg.totalItems ?? 0,
          totalPages: pg.totalPages ?? 1,
          hasNextPage: Boolean(pg.hasNextPage),
          hasPrevPage: Boolean(pg.hasPrevPage),
        })
      }
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [page, appliedSearch])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const columns = [
    {
      key: 'id',
      header: 'Id',
      render: (row) => (
        <span className="font-mono text-xs text-text-muted" title={row.id || ''}>
          {row.id ? String(row.id).slice(0, 8) : '—'}
        </span>
      ),
    },
    { key: 'created_at', header: 'Created', render: (row) => formatDateTime(row.created_at) },
    { key: 'full_name', header: 'Name', render: (row) => cellText(row.full_name, 40) },
    { key: 'email', header: 'Email', render: (row) => cellText(row.email, 36) },
    { key: 'phone_number', header: 'Phone', render: (row) => row.phone_number || '—' },
    { key: 'source', header: 'Source', render: (row) => row.source || '—' },
    { key: 'ip_address', header: 'IP', render: (row) => row.ip_address || '—' },
    {
      key: 'location',
      header: 'Location',
      render: (row) => {
        const location = [row.city, row.state, row.country].filter(Boolean).join(', ') || '—'
        return cellText(location, 60)
      },
    },
    {
      key: 'device',
      header: 'Device',
      render: (row) => {
        const device = [row.browser, row.operating_system, row.device_type].filter(Boolean).join(' · ') || '—'
        return cellText(device, 50)
      },
    },
    { key: 'looking_for', header: 'Looking for', render: (row) => cellText(row.looking_for, 120) },
    {
      key: 'user_agent',
      header: 'User agent',
      render: (row) => <span className="font-mono text-xs">{cellText(row.user_agent, 80)}</span>,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Contact leads"
        subtitle="Submissions from the marketing contact form."
      />

      <SearchField
        className="mb-4"
        value={searchDraft}
        onChange={(e) => setSearchDraft(e.target.value)}
        onSubmit={() => {
          setPage(1)
          setAppliedSearch(searchDraft.trim())
        }}
        onClear={
          searchDraft
            ? () => {
                setSearchDraft('')
                setAppliedSearch('')
                setPage(1)
              }
            : undefined
        }
        placeholder="Name, email, phone, city, browser, message…"
      />

      <Pagination
        className="mb-4"
        pagination={{
          ...pagination,
          totalItems: pagination.totalItems,
        }}
        onPrev={() => pagination.hasPrevPage && setPage((p) => Math.max(1, p - 1))}
        onNext={() => pagination.hasNextPage && setPage((p) => p + 1)}
      />
      {appliedSearch && (
        <p className="mb-2 text-sm text-text-muted dark:text-text-muted-dark">Matching “{appliedSearch}”</p>
      )}

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        minWidth="min-w-[1280px]"
        emptyTitle="No contact leads found"
        getRowKey={(row) => row.id}
      />
    </div>
  )
}

export default ContactLeads
